import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Amount, Button, Chip, Panel, PanelHeader, Ref, Select } from '@promanage/ui';
import { canTransferCommission, formatNzDate, formatPeriod } from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { FinancialNav } from '../features/ledger/FinancialNav';
import { TransferDialog } from '../features/commission/TransferDialog';
import { money } from '../features/commission/format';
import { useSession } from '../features/auth/session';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/financial/commission')({
  component: CommissionPage,
});

/**
 * The agency's own income — what it is holding in the trust account, and taking it out.
 *
 * ONE ACT AND ONE RECORD, and the page is deliberately that small. What the agency holds is a
 * BALANCE, not a query: every fee earned since the last transfer, sitting in accounts that belong
 * to nobody else. So there is no date to choose, no amount to type, no period to select and no
 * report to configure — the button takes all of it, which is the only defensible answer to "how
 * much", and the only thing left to decide is when to press it.
 *
 * THE LINES ARE WHATEVER THE AGENCY HOLDS. Management fees and the GST on them today; a letting-fee
 * ledger when one exists. The screen prints the list the server sends rather than two named
 * figures, so a new fee account appears here without this file changing.
 *
 * UNDER FINANCIAL RATHER THAN ITS OWN RAIL ITEM. Drawing commission is a month-end decision about
 * the LEDGER — the same shape as locking a period, done by the same person on the same evening —
 * and a rail item would put a top-level destination in the rail for something done monthly.
 * Disbursements now sits beside it in the same top bar for the same reason: both are money
 * leaving the trust account against balances read on the Financial page, and the difference
 * between them is only whose money it is.
 */
function CommissionPage() {
  const roles = useSession()?.roles ?? [];
  const mayTransfer = canTransferCommission(roles);

  const [accountId, setAccountId] = useState('');
  const [confirming, setConfirming] = useState(false);

  const position = useQuery(
    trpc.commission.position.queryOptions(
      accountId ? { trustBankAccountId: accountId } : {},
      // Fees land all day. A figure somebody is about to draw against should not be minutes old.
      { staleTime: 5_000 },
    ),
  );

  const transfers = useQuery(
    trpc.commission.transfers.queryOptions(undefined, { staleTime: 15_000 }),
  );

  const data = position.data;
  const accounts = data?.accounts ?? [];
  const ready = data?.available === true;
  const total = ready ? money(data.held.total) : 0n;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Commission"
        subtitle="What the agency has earned, and moving it to the operating account"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {accounts.length > 1 ? (
              <Select
                aria-label="Trust account"
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
                className="w-48"
              >
                <option value="">Choose a trust account…</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>
        }
      />
      <FinancialNav />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── What is standing there ─────────────────────────────────────── */}
        <Panel className="p-0">
          <PanelHeader
            title="Held in trust"
            subtitle={
              ready
                ? `Earned by this agency and still in ${data.trustBankName}`
                : 'Fees earned and not yet drawn'
            }
            className="px-5 pt-5 pb-2"
          />

          <div className="px-5 pb-5">
            {position.isPending ? (
              <div className="h-32 animate-pulse rounded-md bg-surface-sunk" />
            ) : !ready ? (
              <p className="text-[0.8125rem] text-ink-muted">
                {data?.available === false
                  ? data.reason
                  : 'Could not read the fee accounts for this agency.'}
              </p>
            ) : (
              <>
                <Amount
                  value={total}
                  currency
                  className="text-[2rem] font-extrabold tracking-[-0.02em]"
                />

                <dl className="mt-3 flex flex-col gap-1 border-t border-rule pt-3">
                  {/*
                   * ONE ROW PER ACCOUNT, as sent. An account standing at zero keeps its row — it is
                   * money that WILL build up there, and a line that vanished at zero would hide the
                   * account on the day somebody needs to see that it is empty.
                   */}
                  {data.held.lines.map((line) => (
                    <Line key={line.accountId} label={line.name} value={money(line.balance)} />
                  ))}
                  <Line label="Cleared in the trust account" value={money(data.cleared)} muted />
                </dl>

                <p className="mt-3 text-[0.75rem] text-ink-subtle">
                  {/*
                   * SAYS WHAT IT WILL DO, in the two facts that surprise people afterwards: it
                   * takes everything, and it writes one transaction per ledger rather than one for
                   * the lot. Both are visible on the transaction list a minute later.
                   */}
                  A transfer takes all of it and posts into {formatPeriod(data.periodStartsOn)}, one
                  transaction per ledger.
                </p>

                {mayTransfer ? (
                  <Button
                    className="mt-3 w-full"
                    size="sm"
                    disabled={total <= 0n}
                    onClick={() => setConfirming(true)}
                  >
                    Transfer to the operating account
                  </Button>
                ) : (
                  /*
                   * Not a hidden button. Somebody who cannot do this still needs to know the
                   * figure — they are usually the person whose receipting earned it — and being
                   * told who can is the difference between a disabled control and a support ticket.
                   */
                  <p className="mt-3 rounded-md bg-surface-sunk px-3 py-2 text-[0.75rem] text-ink-muted">
                    Drawing on the trust account is a trust administrator’s decision, the same as
                    approving a payment run.
                  </p>
                )}
              </>
            )}
          </div>
        </Panel>

        {/* ── What has already been drawn ────────────────────────────────── */}
        <Panel className="min-w-0 p-0">
          <PanelHeader
            title="Transfers out"
            subtitle="One transaction per ledger drawn"
            className="px-5 pt-5 pb-2"
          />

          {transfers.data && transfers.data.length > 0 ? (
            <ul className="flex flex-col">
              {transfers.data.map((row) => (
                <li key={row.transactionId} className="border-t border-rule">
                  {/*
                   * ONE ROW PER TRANSACTION, which is one row per LEDGER drawn — a sweep of two
                   * accounts is two rows sharing a date. Grouping them back together on the way out
                   * would show a grouping the data does not have, and would take away the thing the
                   * split was for: each row opens, and reverses, on its own.
                   */}
                  <Link
                    to="/financial/transactions"
                    search={{ q: row.ref }}
                    className="flex items-baseline gap-2 px-5 py-2.5 hover:bg-surface-sunk"
                  >
                    <Ref entity="transaction" sequence={row.refSeq} />
                    <span className="min-w-0 flex-1 truncate text-[0.8125rem]">
                      {row.name}
                      <span className="ml-1.5 text-ink-subtle">{formatNzDate(row.drawnOn)}</span>
                      {row.reversedByRef ? (
                        <Chip tone="warn" className="ml-2">
                          Reversed
                        </Chip>
                      ) : null}
                    </span>
                    <Amount
                      value={money(row.amount)}
                      currency
                      className="text-[0.8125rem] font-semibold"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">
              {transfers.isPending
                ? 'Reading…'
                : 'Nothing has been drawn yet. Fees stay in the trust account until they are.'}
            </p>
          )}
        </Panel>
      </div>

      {confirming && ready ? (
        <TransferDialog
          position={data.held}
          trustBankName={data.trustBankName}
          trustBankAccountId={data.trustBankAccountId}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

function Line({ label, value, muted }: { label: string; value: bigint; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt
        className={muted ? 'text-[0.8125rem] text-ink-subtle' : 'text-[0.8125rem] text-ink-muted'}
      >
        {label}
      </dt>
      <dd>
        <Amount
          value={value}
          className={muted ? 'text-[0.8125rem] text-ink-subtle' : 'text-[0.8125rem]'}
        />
      </dd>
    </div>
  );
}
